import api from '@shared/services/api';
import { CalendarEvent, EventCategory } from '../../calendar/types/calendar.types';
import {
  CommentsResponse,
  CreateSpaceData,
  FollowResponse,
  MediaItem,
  SortType,
  Space,
  SpaceComment,
  SpacesResponse,
  UpvoteResponse,
} from '../types/space.types';

/**
 * Service for handling space-related API calls
 */
export class SpaceService {
  /**
   * Get upcoming events and categories from calendar
   */
  static async getUpcomingEvents(): Promise<{ events: CalendarEvent[]; categories: EventCategory[] }> {
    const { data } = await api.get<{ events: CalendarEvent[]; categories: EventCategory[] }>('/calendar');
    return data;
  }
  private static BASE_URL = '/spaces';

  /**
   * Get spaces with pagination and sorting
   */
  static async getSpaces(
    page: number = 1,
    limit: number = 10,
    sortType: SortType = SortType.NEWEST
  ): Promise<SpacesResponse> {
    const { data } = await api.get<SpacesResponse>(
      `${this.BASE_URL}?page=${page}&limit=${limit}&sortType=${sortType}`
    );
    return data;
  }

  /**
   * Get a specific space by ID
   */
  static async getSpace(spaceId: string): Promise<Space | null> {
    try {
      const { data } = await api.get<Space>(`${this.BASE_URL}/${spaceId}`);
      return data;
    } catch (error) {
      console.error('Error fetching space:', error);
      return null;
    }
  }

  /**
   * Get comments for a space with pagination
   */
  static async getSpaceComments(
    spaceId: string,
    page: number = 1,
    limit: number = 10
  ): Promise<CommentsResponse> {
    const { data } = await api.get<CommentsResponse>(
      `${this.BASE_URL}/${spaceId}/comments?page=${page}&limit=${limit}`
    );
    return data;
  }

  /**
   * Get popular spaces
   */
  static async getPopularSpaces(limit: number = 5): Promise<Space[]> {
    try {
      const { data } = await api.get<{ spaces: Space[] }>(
        `${this.BASE_URL}/popular?limit=${limit}`
      );
      return data.spaces;
    } catch (error) {
      console.error('Error fetching popular spaces:', error);
      return [];
    }
  }

  /**
   * Get popular spaces sorted by total interactions (comments + upvotes)
   */
  static async getPopularSpacesByInteractions(limit: number = 5): Promise<Space[]> {
    try {
      const { data } = await api.get<{ spaces: Space[] }>(
        `${this.BASE_URL}/popular/interactions?limit=${limit}`
      );
      return data.spaces;
    } catch (error) {
      console.error('Error fetching popular spaces by interactions:', error);
      return [];
    }
  }

  /**
   * Create a new space
   */
  static async createSpace(spaceData: CreateSpaceData): Promise<Space | null> {
    try {
      const { data } = await api.post<{ space: Space }>(
        this.BASE_URL,
        spaceData
      );
      return data.space;
    } catch (error) {
      console.error('Error creating space:', error);
      return null;
    }
  }

  /**
   * Toggle upvote on a space
   */
  static async toggleUpvote(spaceId: string): Promise<UpvoteResponse> {
    const { data } = await api.post<UpvoteResponse>(
      `${this.BASE_URL}/${spaceId}/upvote`
    );
    return data;
  }

  /**
   * Toggle upvote on a comment
   */
  static async toggleCommentUpvote(commentId: string): Promise<UpvoteResponse> {
    const { data } = await api.post<UpvoteResponse>(
      `${this.BASE_URL}/comments/${commentId}/upvote`
    );
    return data;
  }

  /**
   * Toggle follow on a space
   */
  static async toggleFollow(spaceId: string): Promise<FollowResponse> {
    const { data } = await api.post<FollowResponse>(
      `${this.BASE_URL}/${spaceId}/follow`
    );
    return data;
  }

  static async togglePin(spaceId: string): Promise<{ isPinned: boolean }> {
    const { data } = await api.post<{ success: boolean; isPinned: boolean }>(
      `${this.BASE_URL}/${spaceId}/pin`
    );
    return { isPinned: data.isPinned };
  }

  /**
   * Add a comment to a space
   */
  static async addComment(
    spaceId: string,
    content: string,
    parentId?: string
  ): Promise<SpaceComment | null> {
    try {
      const { data } = await api.post<{ comment: SpaceComment }>(
        `${this.BASE_URL}/${spaceId}/comments`,
        { content, parentId }
      );
      return data.comment;
    } catch (error) {
      console.error('Error adding comment:', error);
      return null;
    }
  }

  /**
   * Delete a comment
   */
  static async deleteComment(spaceId: string, commentId: string): Promise<{ success: boolean }> {
    try {
      const { data } = await api.delete<{ success: boolean }>(`${this.BASE_URL}/${spaceId}/comments/${commentId}`);
      return data;
    } catch (error) {
      console.error('Error deleting comment:', error);
      return { success: false };
    }
  }

  /**
   * Update an existing space
   */
  static async updateSpace(spaceId: string, spaceData: Partial<CreateSpaceData>): Promise<Space | null> {
    try {
      const { data } = await api.put<{ success: boolean; space: Space }>(
        `${this.BASE_URL}/${spaceId}`,
        spaceData
      );
      return data.space;
    } catch (error) {
      console.error('Error updating space:', error);
      return null;
    }
  }

  /**
   * Delete a space
   */
  static async deleteSpace(spaceId: string): Promise<void> {
    await api.delete(`${this.BASE_URL}/${spaceId}`);
  }

  /**
   * Upload media for a space
   */
  static async uploadMedia(file: File): Promise<MediaItem | null> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await api.post<{ media: MediaItem }>(
        `${this.BASE_URL}/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return data.media;
    } catch (error) {
      console.error('Error uploading media:', error);
      return null;
    }
  }
}
